import { create } from 'zustand';
import { Chess } from 'chess.js';
import { getSettings, saveSettings, saveCurrentGame, loadCurrentGame } from '@/utils/storage';
import { DIFFICULTY_LEVELS } from '@/engine/constants';
import { playMoveSound, playCaptureSound, playCheckSound } from '@/utils/sounds';
import { analyzeMoveQuality } from '@/engine/moveQuality';

const initialSettings = getSettings();

export const useGameStore = create((set, get) => ({
  // Game state
  game: new Chess(),
  position: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
  history: [],
  currentMoveIndex: -1,
  
  // Game info
  playerColor: 'white',
  gameStatus: 'idle', // idle, playing, checkmate, stalemate, draw, resigned
  winner: null,
  gameStartTime: null, // Track game duration
  
  // AI
  aiDifficulty: initialSettings.aiDifficulty || 'medium',
  isAIThinking: false,
  
  // Coach mode
  coachEnabled: initialSettings.coachEnabled ?? true,
  coachLevel: initialSettings.coachLevel || 3, // 1, 3, or 5 moves ahead
  arrowCount: initialSettings.arrowCount || 3, // Number of PV arrows to show (1-5)
  showDangerHighlights: initialSettings.showDangerHighlights ?? true, // Show pieces in danger
  lastMoveQuality: null,
  suggestedMove: null,
  evaluation: 0,
  showSuggestion: false,
  
  // Game Review Mode
  reviewMode: false,
  reviewMoveIndex: 0,
  moveAnalysis: [], // Store analysis for each move
  
  // Settings
  boardTheme: initialSettings.boardTheme || 'cosmic',
  soundEnabled: initialSettings.soundEnabled ?? true,
  showLegalMoves: initialSettings.showLegalMoves ?? true,
  showCoordinates: initialSettings.showCoordinates ?? true,
  
  // Selection
  selectedSquare: null,
  legalMoves: [],
  lastMove: null,
  
  // Blunder tracking
  blunderCount: 0,
  
  // Actions
  startNewGame: (playerColor = 'white', difficulty = 'medium') => {
    const newGame = new Chess();
    set({
      game: newGame,
      position: newGame.fen(),
      history: [],
      currentMoveIndex: -1,
      playerColor,
      aiDifficulty: difficulty,
      gameStatus: 'playing',
      winner: null,
      gameStartTime: Date.now(), // Track start time
      lastMoveQuality: null,
      suggestedMove: null,
      evaluation: 0,
      showSuggestion: false,
      selectedSquare: null,
      legalMoves: [],
      lastMove: null,
      blunderCount: 0
    });
    
    // If playing as black, AI moves first
    if (playerColor === 'black') {
      get().triggerAIMove();
    }
  },
  
  makeMove: (from, to, promotion = 'q') => {
    const { game, playerColor, gameStatus, soundEnabled, suggestedMove, history, moveAnalysis } = get();
    if (gameStatus !== 'playing') return false;
    
    const isPlayerTurn = (game.turn() === 'w' && playerColor === 'white') ||
                         (game.turn() === 'b' && playerColor === 'black');
    
    if (!isPlayerTurn) return false;
    
    try {
      // Analyze move quality BEFORE making the move
      const isOpeningPhase = history.length < 10;
      const moveQuality = analyzeMoveQuality(game, from, to, suggestedMove, isOpeningPhase);
      
      const move = game.move({ from, to, promotion });
      if (move) {
        const newHistory = [...get().history, move.san];
        
        // Store analysis for game review
        const newAnalysis = [...moveAnalysis, {
          move: move.san,
          from,
          to,
          quality: moveQuality,
          fen: game.fen()
        }];
        
        set({
          position: game.fen(),
          history: newHistory,
          currentMoveIndex: newHistory.length - 1,
          lastMove: { from, to },
          selectedSquare: null,
          legalMoves: [],
          showSuggestion: false,
          lastMoveQuality: moveQuality,
          moveAnalysis: newAnalysis,
          blunderCount: moveQuality?.quality === 'blunder' ? get().blunderCount + 1 : get().blunderCount
        });
        
        // Play sounds
        if (soundEnabled) {
          if (move.captured) {
            playCaptureSound();
          } else {
            playMoveSound();
          }
          if (game.isCheck()) {
            playCheckSound();
          }
        }
        
        // Check game end
        get().checkGameEnd();
        
        // Auto-save
        saveCurrentGame({
          fen: game.fen(),
          history: newHistory,
          playerColor,
          aiDifficulty: get().aiDifficulty
        });
        
        return move;
      }
    } catch (e) {
      console.error('Invalid move:', e);
    }
    return false;
  },
  
  makeAIMove: (move) => {
    const { game } = get();
    
    try {
      const result = game.move(move);
      if (result) {
        const newHistory = [...get().history, result.san];
        
        // Clear the old suggestion when AI moves - player needs fresh analysis
        set({
          position: game.fen(),
          history: newHistory,
          currentMoveIndex: newHistory.length - 1,
          lastMove: { from: move.from, to: move.to },
          isAIThinking: false,
          suggestedMove: null, // Clear old suggestion immediately
          lastMoveQuality: null // Clear old quality too
        });
        
        get().checkGameEnd();
        
        saveCurrentGame({
          fen: game.fen(),
          history: newHistory,
          playerColor: get().playerColor,
          aiDifficulty: get().aiDifficulty
        });
        
        return result;
      }
    } catch (e) {
      console.error('AI move error:', e);
    }
    
    set({ isAIThinking: false });
    return null;
  },
  
  triggerAIMove: () => {
    set({ isAIThinking: true });
  },
  
  setAIThinking: (value) => set({ isAIThinking: value }),
  
  checkGameEnd: () => {
    const { game, playerColor, aiDifficulty, history, gameStartTime } = get();
    
    let gameEnded = false;
    let result = null;
    let winner = null;
    
    if (game.isCheckmate()) {
      winner = game.turn() === 'w' ? 'black' : 'white';
      result = winner === playerColor ? 'win' : 'loss';
      gameEnded = true;
      set({
        gameStatus: 'checkmate',
        winner
      });
    } else if (game.isStalemate()) {
      result = 'draw';
      gameEnded = true;
      set({ gameStatus: 'stalemate', winner: null });
    } else if (game.isDraw()) {
      result = 'draw';
      gameEnded = true;
      set({ gameStatus: 'draw', winner: null });
    }
    
    // Record stats if game ended
    if (gameEnded && result) {
      const duration = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;
      
      // Record stats asynchronously (don't block UI)
      import('@/utils/statsService').then(({ recordGameResult }) => {
        recordGameResult({
          result,
          difficulty: aiDifficulty,
          playerColor,
          totalMoves: history.length,
          gameDurationSeconds: duration,
          openingName: null // Could add opening detection later
        });
      }).catch(e => console.error('Stats recording failed:', e));
    }
  },
  
  resign: () => {
    const { playerColor, gameStatus, aiDifficulty, history, gameStartTime } = get();
    if (gameStatus !== 'playing') return;
    
    const winner = playerColor === 'white' ? 'black' : 'white';
    set({
      gameStatus: 'resigned',
      winner
    });
    
    // Record loss for resignation
    const duration = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;
    import('@/utils/statsService').then(({ recordGameResult }) => {
      recordGameResult({
        result: 'loss',
        difficulty: aiDifficulty,
        playerColor,
        totalMoves: history.length,
        gameDurationSeconds: duration,
        openingName: null
      });
    }).catch(e => console.error('Stats recording failed:', e));
  },
  
  selectSquare: (square) => {
    const { game, selectedSquare, playerColor } = get();
    const isPlayerTurn = (game.turn() === 'w' && playerColor === 'white') ||
                         (game.turn() === 'b' && playerColor === 'black');
    
    if (!isPlayerTurn || get().gameStatus !== 'playing') {
      set({ selectedSquare: null, legalMoves: [] });
      return;
    }
    
    // If clicking on same square, deselect
    if (selectedSquare === square) {
      set({ selectedSquare: null, legalMoves: [] });
      return;
    }
    
    // If we have a selected square and clicking on a legal move, make the move
    if (selectedSquare && get().legalMoves.includes(square)) {
      get().makeMove(selectedSquare, square);
      return;
    }
    
    // Select new square if it has player's piece
    const piece = game.get(square);
    const playerPieceColor = playerColor === 'white' ? 'w' : 'b';
    
    if (piece && piece.color === playerPieceColor) {
      const moves = game.moves({ square, verbose: true });
      set({
        selectedSquare: square,
        legalMoves: moves.map(m => m.to)
      });
    } else {
      set({ selectedSquare: null, legalMoves: [] });
    }
  },
  
  setEvaluation: (eval_) => set({ evaluation: eval_ }),
  setSuggestedMove: (move) => set({ suggestedMove: move }),
  setLastMoveQuality: (quality) => set({ lastMoveQuality: quality }),
  setShowSuggestion: (show) => set({ showSuggestion: show }),
  incrementBlunderCount: () => set(state => ({ blunderCount: state.blunderCount + 1 })),
  
  // Settings
  setCoachEnabled: (enabled) => {
    set({ coachEnabled: enabled });
    saveSettings({ ...getSettings(), coachEnabled: enabled });
  },
  
  setCoachLevel: (level) => {
    set({ coachLevel: level });
    saveSettings({ ...getSettings(), coachLevel: level });
  },
  
  setArrowCount: (count) => {
    set({ arrowCount: count });
    saveSettings({ ...getSettings(), arrowCount: count });
  },
  
  setShowDangerHighlights: (show) => {
    set({ showDangerHighlights: show });
    saveSettings({ ...getSettings(), showDangerHighlights: show });
  },
  
  setSoundEnabled: (enabled) => {
    set({ soundEnabled: enabled });
    saveSettings({ ...getSettings(), soundEnabled: enabled });
  },
  
  setBoardTheme: (theme) => {
    set({ boardTheme: theme });
    saveSettings({ ...getSettings(), boardTheme: theme });
  },
  
  setShowLegalMoves: (show) => {
    set({ showLegalMoves: show });
    saveSettings({ ...getSettings(), showLegalMoves: show });
  },
  
  setShowCoordinates: (show) => {
    set({ showCoordinates: show });
    saveSettings({ ...getSettings(), showCoordinates: show });
  },
  
  setAIDifficulty: (difficulty) => {
    set({ aiDifficulty: difficulty });
    saveSettings({ ...getSettings(), aiDifficulty: difficulty });
  },
  
  // Load game
  loadGame: (gameData) => {
    const newGame = new Chess();
    
    // Replay all moves
    if (gameData.history) {
      for (const san of gameData.history) {
        newGame.move(san);
      }
    } else if (gameData.fen) {
      newGame.load(gameData.fen);
    }
    
    set({
      game: newGame,
      position: newGame.fen(),
      history: gameData.history || [],
      currentMoveIndex: (gameData.history?.length || 0) - 1,
      playerColor: gameData.playerColor || 'white',
      aiDifficulty: gameData.aiDifficulty || 'medium',
      gameStatus: newGame.isGameOver() ? 'checkmate' : 'playing',
      lastMove: null,
      selectedSquare: null,
      legalMoves: []
    });
  },
  
  // Undo (go back 2 moves - player + AI)
  undo: () => {
    const { game, history, playerColor } = get();
    if (history.length < 2) return;
    
    game.undo(); // Undo AI move
    game.undo(); // Undo player move
    
    const newHistory = history.slice(0, -2);
    
    set({
      position: game.fen(),
      history: newHistory,
      currentMoveIndex: newHistory.length - 1,
      lastMove: null,
      lastMoveQuality: null,
      suggestedMove: null
    });
  },
  
  // Get current game data for saving
  getGameData: () => {
    const state = get();
    return {
      fen: state.position,
      history: state.history,
      playerColor: state.playerColor,
      aiDifficulty: state.aiDifficulty,
      gameStatus: state.gameStatus
    };
  },
  
  // Set last move quality (for dismissing feedback)
  setLastMoveQuality: (quality) => {
    set({ lastMoveQuality: quality });
  },
  
  // Game Review Mode
  enterReviewMode: () => {
    const { history, moveAnalysis } = get();
    if (history.length === 0) return;
    
    set({
      reviewMode: true,
      reviewMoveIndex: history.length - 1
    });
  },
  
  exitReviewMode: () => {
    set({
      reviewMode: false,
      reviewMoveIndex: 0
    });
  },
  
  goToMove: (index) => {
    const { history, game } = get();
    if (index < 0 || index >= history.length) return;
    
    // Reconstruct position at that move
    const tempGame = new Chess();
    for (let i = 0; i <= index; i++) {
      tempGame.move(history[i]);
    }
    
    set({
      reviewMoveIndex: index,
      position: tempGame.fen(),
      lastMove: null
    });
  },
  
  nextReviewMove: () => {
    const { reviewMoveIndex, history } = get();
    if (reviewMoveIndex < history.length - 1) {
      get().goToMove(reviewMoveIndex + 1);
    }
  },
  
  prevReviewMove: () => {
    const { reviewMoveIndex } = get();
    if (reviewMoveIndex > 0) {
      get().goToMove(reviewMoveIndex - 1);
    }
  },
  
  firstReviewMove: () => {
    get().goToMove(0);
  },
  
  lastReviewMove: () => {
    const { history } = get();
    get().goToMove(history.length - 1);
  }
}));
